import configService from "./ConfigService";
import { UserManager, WebStorageStateStore, Log } from "oidc-client";

export default class AuthService {
    UserManager;
    accessToken;
    idConfig;

    static __instance = null;
    static __silentRefreshUnderway = false;
    
    constructor() {
        // oidc-client logging
        Log.logger = console;
        Log.level = Log.DEBUG;
    }

    getAuthUrl = () =>{
        return this.idConfig.authority;
    }

    getRedirectUrl = () => {
        return this.idConfig.redirect_uri;
    }
    init = async () => {
        this.idConfig = await configService.getIdentityConfig();
        //console.log('initing usermanager with id config!', this.idConfig);
        this.UserManager = new UserManager({
            ...this.idConfig,
            userStore: new WebStorageStateStore({ store: window.localStorage })
        });

        this.UserManager.events.addUserLoaded(user => {
            this.accessToken = user.access_token;
            localStorage.setItem("access_token", user.access_token);
            localStorage.setItem("id_token", user.id_token);
            this.setUserInfo({
                accessToken: this.accessToken,
                idToken: user.id_token
            });            
            if (window.location.href.indexOf("signin-oidc") !== -1) {
                this.navigateToScreen();
            }
        });
        this.UserManager.events.addSilentRenewError(async (e) => {
            console.error("silent renew error", e.message);
            // this.UserManager.removeUser();
            // this.UserManager.clearStaleState();
            // retry
            await this.signinSilent();
        });

        this.UserManager.events.addAccessTokenExpired(async () => {
            if(AuthService.__silentRefreshUnderway === false) {
                AuthService.__silentRefreshUnderway = true;
                // this probably means app has been closed for a while and renew hasn't happened(?)
                console.log("token expired, trying to refresh");
                try {
                    await this.signinSilent();
                    //console.log('signInSilentResult', signInSilentResult);
                    AuthService.__silentRefreshUnderway = false;
                }
                catch(err) {
                    console.error('token refresh failed!', err);
                    await this.UserManager.removeUser();
                    await this.UserManager.clearStaleState();
                    await this.signinRedirect();
                }
            }
        });
        await new Promise(resolve => setTimeout(resolve, 1000));
    }

    static async getInstance() {
        //console.log('getting instance');
        // if there's one initing, await it
        if(window.__initTask)
            await window.__initTask;
        if (!window.__instance) {
            var authService = new AuthService();
            window.__initTask = authService.init();
            await window.__initTask;
            //await authService.getUser();
            window.__instance = authService;
        }
        return window.__instance;
    }

    signinRedirectCallback = async () => {
        try {
            var user = await this.UserManager.signinRedirectCallback();
            console.log('signed in.', user);
            return user;            
        }
        catch(error) {
            console.error('signinRedirectCallback failed', error);
            await this.UserManager.signinRedirect();
        }
    };

    //Call this to get an authenticated user (and prompt for login\register if not already)
    getAuthenticatedUser = async(confirm) => {
        const loginCancelledMessage = 'Login cancelled. You must login (or register) to do this.';
        try {            
            const user = await this.UserManager.getUser();
            if(user === null)
                // only prompt for confirmation if confirm flag passed in = true
                if(confirm && confirm === true) 
                    if(window.confirm('You need to be logged in to do this. Would you like to login now?')){
                        this.UserManager.signinRedirect();
                        return new Promise(resolve => setTimeout(resolve, 2000));
                    }
                    else
                        throw loginCancelledMessage;
                else{
                    this.UserManager.signinRedirect();
                    return new Promise(resolve => setTimeout(resolve, 2000));
                }
            return user;
        }
        catch(error){
            if(error === loginCancelledMessage)
                throw error;
            else {
                console.error('Assuming not logged in: ', error);
                this.UserManager.signinRedirect();
                return new Promise(resolve => setTimeout(resolve, 2000));
            }
        }
    }

    getUser = async () => {
        try {
            const user = await this.UserManager.getUser();
            return user;
        }
        catch(error){
            console.log('Assuming not logged in: ', error);
            return null;            
        }
    };

    parseJwt = token => {
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace("-", "+").replace("_", "/");
        return JSON.parse(window.atob(base64));
    };

    setUserInfo = authResult => {
        const data = this.parseJwt(this.accessToken);

        this.setSessionInfo(authResult);
        this.setUser(data);
    };

    signinRedirect = () => {        
        if(!window.location.pathname.toString().endsWith("loggedout"))
            localStorage.setItem("redirectUri", window.location.pathname);
        this.UserManager.signinRedirect({});
    };

    setUser = data => {
        localStorage.setItem("userId", data.sub);
    };

    navigateToScreen = () => {        
        const redirectUri = !!localStorage.getItem("redirectUri")
            ? localStorage.getItem("redirectUri")
            : "/en/dashboard";
        console.log('navigating to screen', redirectUri);
        const language = "/" + redirectUri.split("/")[1];

        window.location.replace(language + "/dashboard");
    };

    setSessionInfo(authResult) {
        localStorage.setItem("access_token", authResult.accessToken);
        localStorage.setItem("id_token", authResult.idToken);
    }

    signinSilent = () => {
        return this.UserManager.signinSilent();
    };
    signinSilentCallback = () => {
        return this.UserManager.signinSilentCallback();
    };

    createSigninRequest = () => {
        return this.UserManager.createSigninRequest();
    };

    logout = async () => {
        await this.UserManager.removeUser();
        // also clear redirectUri => probably not valid any more!
        localStorage.setItem("redirectUri", null);
        await this.UserManager.signoutRedirect({
            id_token_hint: localStorage.getItem("id_token")
        });
        this.UserManager.clearStaleState();
    };

    signoutRedirectCallback = () => {
        this.UserManager.signoutRedirectCallback().then(() => {
            localStorage.clear();
            window.location.replace(process.env.REACT_APP_PUBLIC_URL);
        });
        this.UserManager.clearStaleState();
    };

    getToken = () => {
        return localStorage.getItem("access_token");
    }
}